import {graphql} from 'gatsby';
import React, {memo, useCallback, useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';

import MapArtist from '../components/map-artist';
import PageElementGroup from '../components/page-element-group';
import * as iter from '../utils/iter';

import Prng from '../utils/prng';

import style from './map.module.scss';

/**
 * @typedef {(val: string | null) => void} SetOpenArtist
 */

const Artist = memo(
	/**
	 * @param {{
	 *   openArtist: null | string,
	 *   openArtistAudio: boolean,
	 *   setOpenArtist: SetOpenArtist,
	 *   selectMapTarget: (slug: string) => void,
	 *   artist: Pick<import("../../tmp/graphql-types").ArtistsJson, "id" | "slug"> &
	 *     import("../../tmp/graphql-types").MapArtistFragment,
	 * }} props
	 * @returns {React.ReactElement} ...
	 */
	function Artist({artist, openArtist, openArtistAudio, setOpenArtist, selectMapTarget}) {
		const toggleOpen = useCallback(
			() => {
				if (openArtist === artist.slug) {
					setOpenArtist(null);
				} else {
					setOpenArtist(artist.slug);
				}
			},
			[openArtist, setOpenArtist, artist.slug],
		);

		const selectMapTarget2 = useCallback(
			() => {
				selectMapTarget(artist.slug);
			},
			[selectMapTarget, artist.slug],
		);

		return (
			<MapArtist
				open={openArtist === artist.slug}
				toggleOpen={toggleOpen}
				artist={artist}
				openAudio={openArtistAudio}
				selectMapTarget={selectMapTarget2}
			/>
		);
	}
);

export const query = graphql`
	query MapPage {
		allArtistsJson(sort: {order: ASC, fields: order}) {
			nodes {
				id
				slug
				fixedProject
				...MapArtist
			}
		}

		allPagesJson(filter: {slug: {eq: "map"}}) {
			nodes {
				...PageElementGroup
			}
		}
	}
`;

export default memo(
	/**
	 * @param {import("gatsby").PageProps<
	 *   import("../../tmp/graphql-types").MapPageQuery,
	 *   {
	 *     hashState: number,
	 *     meta: import("../page-metadata").PageMetadata,
	 *   }
	 * >} props
	 * @returns {React.ReactElement} ...
	 */
	function MapPage({data, location, pageContext: {hashState, meta}}) {
		const prng = new Prng(hashState);

		/** @type {React.MutableRefObject<HTMLIFrameElement | null>} */
		const mapIframeRef = useRef(null);
		/** @type {React.MutableRefObject<HTMLIFrameElement | null>} */
		const mapIframeAnchorRef = useRef(null);

		const [openArtist, setOpenArtist] = useState(
			/** @returns {string | null} */
			() => null
		);

		const [openArtistAudio, setOpenArtistAudio] = useState(false);

		// open hash-linked artist on page load
		useEffect(
			() => {
				const hash = location.hash.substring(1);

				const AUDIO_HASH_SUFFIX = '-audio';

				if (hash.endsWith(AUDIO_HASH_SUFFIX)) {
					setOpenArtistAudio(true);
					setOpenArtist(hash.substr(0, hash.length - AUDIO_HASH_SUFFIX.length));
				} else {
					setOpenArtistAudio(false);
					setOpenArtist(hash);
				}
			},
			[location.hash, setOpenArtist, setOpenArtistAudio],
		);

		useEffect(
			() => {
				if (openArtist && mapIframeRef.current?.contentWindow) {
					mapIframeRef.current.contentWindow.postMessage(
						{type: 'selectTarget', slug: openArtist},
						'*',
					);
				}
			},
			[openArtist],
		);

		const intl = useIntl();
		const mapEmbedSrc = intl.locale === 'en' ?
			'https://www.braunschweig.de/lichtparcours2020/map-embed-en.php?braunschweig-iframe-embed' :
			'https://www.braunschweig.de/lichtparcours2020/map-embed.php?braunschweig-iframe-embed';

		const selectMapTarget = useCallback(
			/**
			 * @param {string} slug
			 */
			(slug) => {
				if (mapIframeAnchorRef.current) {
					mapIframeAnchorRef.current.scrollIntoView({
						behavior: 'smooth',
						block: 'start',
						inline: 'nearest',
					});
				}

				// the artist should be selected already as the effect when
				// opening the accordion item should already have sent the
				// message, but the iframe might not have been initialized etc.
				if (mapIframeRef.current?.contentWindow) {
					mapIframeRef.current.contentWindow.postMessage(
						{type: 'selectTarget', slug: slug},
						'*',
					);
				}
			},
			[],
		);

		const onOpenArtistChange = useCallback(
			/**
			 * @param {string | null} slug
			 * @returns {void}
			 */
			(slug) => {
				setOpenArtist(slug);
				setOpenArtistAudio(false);
			},
			[setOpenArtist, setOpenArtistAudio],
		);

		return (
			<div className={style.base}>
				<span
					className={style.mapIframeAnchor}
					id="map"
					ref={mapIframeAnchorRef}
				/>

				<iframe
					className={style.mapIframe}
					sandbox="allow-same-origin allow-scripts allow-top-navigation allow-popups"
					allow="fullscreen, geolocation, gyroscope, ambient-light-sensor"
					src={mapEmbedSrc}
					ref={mapIframeRef}
					loading="lazy"
					importance="low"
				/>

				<PageElementGroup
					name="start"
					nodes={data.allPagesJson.nodes}
					meta={meta}
					hashState={prng.next()}
				/>

				<section className={style.artists}>
					{iter.from(data.allArtistsJson.nodes)
						.filter((a) => !a.fixedProject)
						.map((a) => (
							<Artist
								key={a.id}
								artist={a}
								setOpenArtist={onOpenArtistChange}
								openArtist={openArtist}
								openArtistAudio={openArtistAudio}
								selectMapTarget={selectMapTarget}
							/>
						))
						.toArray()
					}
				</section>

				<PageElementGroup
					name="middle"
					nodes={data.allPagesJson.nodes}
					meta={meta}
					hashState={prng.next()}
				/>

				<section className={style.artists}>
					{iter.from(data.allArtistsJson.nodes)
						.filter((a) => a.fixedProject)
						.map((a) => (
							<Artist
								key={a.id}
								artist={a}
								setOpenArtist={setOpenArtist}
								openArtist={openArtist}
								openArtistAudio={openArtistAudio}
								selectMapTarget={selectMapTarget}
							/>
						))
						.toArray()
					}
				</section>
			</div>
		);
	}
);
